import type { IReportSearchBarSchema } from '@/interfaces/api/schemas/report'
import type { IGroup } from '@/interfaces/entities/group'
import { useAlarmStore } from '@/store/alarm'
import { useReportStore } from '@/store/report'
import { formatPeriodDate } from '@/utils/date/date-time'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

export interface IReportSearchOrchestrationPayload {
  base: IReportSearchBarSchema
  compare?: IReportSearchBarSchema
  isCompareEnabled: boolean
}

export function useReportSearchOrchestration() {
  const reportStore = useReportStore()
  const alarmStore = useAlarmStore()
  const { showAlarmReportBase, showAlarmReportCompare } = storeToRefs(reportStore)

  const isLoading = ref(false)

  async function executeSearch(payload: IReportSearchOrchestrationPayload) {
    const { base, compare, isCompareEnabled } = payload

    isLoading.value = true
    try {
      reportStore.setBasePeriod(base.period)
      reportStore.setBaseGroup(base.group)
      reportStore.setBaseShift(base.shift)
      alarmStore.setCurrentGroupFilter(base.group)
      alarmStore.setCurrentShiftFilter(base.shift)
      alarmStore.setLastSearchAlarmsType('all')

      await reportStore.fetchBaseReport()

      if (showAlarmReportBase.value) {
        await alarmStore.fetchBaseAlarmReportMetrics(formatPeriodDate(base.period))
      }

      if (isCompareEnabled && compare) {
        reportStore.setComparePeriod(compare.period)
        reportStore.setCompareGroup(compare.group)
        reportStore.setCompareShift(compare.shift)
        alarmStore.setCurrentGroupFilter(compare.group)
        alarmStore.setCurrentShiftFilter(compare.shift)

        await reportStore.fetchCompareReport()

        if (showAlarmReportCompare.value) {
          await alarmStore.fetchCompareAlarmReportMetrics(formatPeriodDate(compare.period))
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  function clearCompare() {
    reportStore.removeCompareReport()
    reportStore.setCompareGroup({} as IGroup)
    reportStore.setComparePeriod([])
    reportStore.setCompareShift(null)
    reportStore.setShowAlarmReportCompare(false)
  }

  return {
    isLoading,
    executeSearch,
    clearCompare,
  }
}
